/**
 * Shared market-services hook for service detail pages. Resolves the visitor's market
 * catalog once and hands the flattened service list to the price and availability hooks.
 * A module-level promise cache keyed by market id dedupes the fetch across every consumer
 * on a page (the hero card, each cross-sell card, and the page-level availability filter),
 * so one market resolution triggers a single fetchServicesForMarket call rather than one
 * per component. Returns null until a market resolves and its catalog loads, matching the
 * baseline SSG paint ("don't filter / show the neutral price").
 */
import { useEffect, useState } from 'react'
import { useMarketContext } from '../components/MarketContext'
import { fetchServicesForMarket } from '../services/GraphQlClientService'

const marketServicesCache = new Map()

/**
 * Loads (once per market id) the market's grouped services response and flattens it to a
 * single array. The in-flight promise is cached so concurrent callers share one request;
 * a rejected fetch is evicted so a later render can retry.
 * @param {string} marketId
 * @returns {Promise<Array<object>>}
 */
const loadMarketServices = marketId => {
  if (!marketServicesCache.has(marketId)) {
    marketServicesCache.set(
      marketId,
      fetchServicesForMarket(marketId)
        .then(grouped => Object.values(grouped || {}).flat())
        .catch(err => {
          marketServicesCache.delete(marketId)
          throw err
        })
    )
  }
  return marketServicesCache.get(marketId)
}

/**
 * @returns {{ marketServices: Array<object>|null, market: object|null }} the flattened market
 *   service list (null until a market resolves and the fetch completes) and the selected market.
 */
export const useMarketServices = () => {
  const { selectedMarket } = useMarketContext()
  const marketId = selectedMarket?.id
  const [marketServices, setMarketServices] = useState(null)

  useEffect(() => {
    let cancelled = false
    // Drop the previous market's catalog immediately so consumers fall back to the
    // neutral SSG paint instead of rendering stale prices/availability during the swap.
    setMarketServices(null)
    if (!marketId) {
      return undefined
    }
    loadMarketServices(marketId)
      .then(list => {
        if (!cancelled) {
          setMarketServices(list)
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [marketId])

  return { marketServices, market: selectedMarket }
}
